import React from 'react';
import type { CalendarRow, FuelDay, FuelPlan } from '../types';

const fuelDays: FuelDay[] = [
  {
    day: 'Thursday 23',
    tag: 'T-3 · ramp',
    kcal: '2,830 kcal',
    meals: [
      { time: '07:30', description: '3 slices GF toast · jam · firm banana · black coffee', cho: 130 },
      { time: '10:00', description: '2 rice cakes · jam · kiwi · 250 mL sports drink', cho: 85 },
      { time: '13:00', description: '2 c jasmine rice · 100 g cooked chicken · soy · orange', cho: 100 },
      { time: '16:00', description: '3 slices GF toast · maple · jam · firm banana · 500 mL sports drink', cho: 135 },
      {
        time: '19:00',
        description: '200 g GF pasta · tomato sauce · 1 tin tuna (~100 g) · 2 rice bars · strawberries',
        cho: 105,
      },
    ],
    totals: { cho: '555 g', protein: '82 g', fat: '24 g', fluid: '3.0 L' },
    note: "Thu is a prep/ramp day, not full load — dinner is booked at Bubala Spitalfields. Steer towards the carb-leaning plates (hummus, confit potato latkes, cabbage, any rice-based sides) and don't stress the exact gram count. Friday is where the real load kicks in.",
  },
  {
    day: 'Friday 24',
    tag: 'T-2 · full load',
    kcal: '3,790 kcal',
    meals: [
      {
        time: '07:30',
        description: '4 slices GF toast · jam · maple · firm banana · 250 mL milk · 250 mL OJ',
        cho: 165,
      },
      { time: '10:00', description: '3 slices GF toast · jam · maple · 500 mL sports drink', cho: 130 },
      { time: '12:30', description: '2.5 c rice · 100 g cooked chicken · garlic oil · orange', cho: 150 },
      { time: '15:00', description: '3 rice cakes · maple · firm banana · 500 mL sports drink', cho: 95 },
      { time: '17:30', description: '2 rice bars · firm banana · 500 mL sports drink', cho: 90 },
      {
        time: '19:30',
        description: '200 g GF pasta · sauce · 100 g cooked chicken · 2 slices GF toast w/ maple',
        cho: 155,
      },
    ],
    totals: { cho: '785 g', protein: '89 g', fat: '26 g', fluid: '3.5 L' },
  },
  {
    day: 'Saturday 25',
    tag: 'T-1 · leanest · low fibre · in bed 22:00',
    kcal: '3,850 kcal · last bite 19:45',
    meals: [
      {
        time: '07:30',
        description: '4 slices GF toast · jam · maple · firm banana · 250 mL milk · salt · 250 mL sports drink',
        cho: 190,
      },
      { time: '10:00', description: '2 slices GF toast · jam · maple · 500 mL sports drink · coffee', cho: 115 },
      { time: '12:30', description: '2.5 c jasmine rice · 100 g cooked chicken · salt · soy · strawberries', cho: 145 },
      { time: '15:00', description: '3 rice cakes · maple · firm banana · 500 mL sports drink', cho: 95 },
      { time: '17:00', description: '2.5 c rice · 1 tin tuna (in spring water) · salt · 3 GF toast w/ jam', cho: 175 },
      { time: '19:30', description: '3 rice bars · firm banana · 250 mL sports drink · final Nomio PM shot', cho: 90 },
    ],
    totals: { cho: '810 g', protein: '84 g', fat: '19 g', fluid: '3.5 L', sodium: '4.0 g Na' },
  },
];

const calendarDates = ['Sun 19', 'Mon 20', 'Tue 21', 'Wed 22', 'Thu 23', 'Fri 24', 'Sat 25', 'Sun 26'];

const calendarRows: CalendarRow[] = [
  {
    name: 'Nomio · 1 shot ×2/day',
    sub: 'AM + PM, 12 h apart · start Sun 19 evening',
    cells: [
      { label: 'START', tone: 'on' },
      { label: 'AM+PM', tone: 'on' },
      { label: 'AM+PM', tone: 'on' },
      { label: 'AM+PM', tone: 'on' },
      { label: 'AM+PM', tone: 'on' },
      { label: 'AM+PM', tone: 'on' },
      { label: 'AM+PM', tone: 'on' },
      { label: '·', tone: 'dot' },
    ],
  },
  {
    name: 'Beetroot · 70 mL shot',
    sub: 'Beet-It Sport · same time AM · 8 mmol nitrate',
    cells: [
      { label: '·', tone: 'dot' },
      { label: 'START', tone: 'on' },
      { label: 'AM', tone: 'on' },
      { label: 'AM', tone: 'on' },
      { label: 'AM', tone: 'on' },
      { label: 'AM', tone: 'on' },
      { label: 'AM', tone: 'on' },
      { label: '06:30 →', tone: 'am' },
    ],
  },
];

export const londonFuelling: FuelPlan = {
  referenceWeightKg: 70,
  waves: [{ id: 'mass', label: 'Mass start', time: '09:36' }],
  defaultWaveId: 'mass',
  ticker: [
    { label: 'Thu · ramp', value: '555', unit: 'g CHO', sub: '8 g/kg · 2,830 kcal' },
    { label: 'Fri · load', value: '785', unit: 'g CHO', sub: '11 g/kg · 3,790 kcal' },
    { label: 'Sat · peak', value: '810', unit: 'g CHO', sub: '11.5 g/kg · 3,850 kcal · fibre ≤10' },
    { label: 'Sun AM', value: '175', unit: 'g + gel', sub: 'pre-race · 2.5 g/kg' },
  ],
  calendarDates,
  calendarRows,
  calendarSub: 'Start dates, doses, and daily rhythm for Nomio and beetroot.',
  days: fuelDays,
  daysSub: 'Meal-by-meal plan for the three build days — low-FODMAP, low fibre, lactose permitted.',
  morningTitle: 'Race morning · Sunday 26',
  morningSub:
    'Kit laid out the night before · bib · gels pre-counted (caf and non-caf labelled) · beetroot shot · PURE sachets.',
  morning: [
    { offset: -276, what: 'Wake · 300 mL water + pinch salt', note: 'Rehydrate' },
    {
      offset: -216,
      what: 'Pre-race: 2 GF toast · 40 mL maple · jam · banana · 30 g PURE in 300 mL water · strong coffee (~100 mg caf)',
      note: '175 g CHO',
      variant: 'key',
    },
    { offset: -186, what: 'Beetroot shot 70 mL', note: '~8 mmol NO₃', variant: 'key' },
    { offset: -171, until: -126, what: 'Sip 400 mL water + 500 mg Na tab · travel to start', note: '500 mg Na' },
    { offset: -126, what: 'Stop bulk fluid · bathroom · small sips' },
    {
      offset: -96,
      what: '½ PURE sachet (~45 g) in 500 mL water · sip ≤150 mL · light walk',
      note: 'PV expand · Na built-in',
      variant: 'key',
    },
    { offset: -51, what: 'Warm-up: 8–10 min jog · drills · 4 × 100 m strides', note: 'Sip 100 mL' },
    { offset: -21, what: 'Final bathroom · into corral' },
    { offset: -14, what: 'Caf PF 30 gel + 100 mL water', note: '+100 mg caf', variant: 'key' },
    { offset: 0, what: 'GUN', variant: 'gun' },
  ],
  raceRules: [
    {
      label: 'In-race',
      body: (
        <>
          First gel at 15 min · then every 25 min · target <strong>90 g CHO/h</strong> · drink at every feed · 500–600 mg
          Na/h · 600–800 mL/h.
        </>
      ),
    },
    {
      label: 'Gel plan',
      body: (
        <>
          1 caf pre-start (09:22) + 6 in-race: <strong>non-caf × 3 · caf · non-caf × 2</strong> · caf hits km 25 peak
          zone.
        </>
      ),
    },
  ],
  panels: [
    {
      kind: 'list',
      icon: 'pill',
      title: 'Race-day stack',
      tag: 'supplements',
      rows: [
        { key: '06:00', text: 'Pre-race meal + strong coffee (~100 mg caf)' },
        { key: '06:30', text: 'Beetroot shot 70 mL' },
        { key: '08:00', text: '½ PURE sachet in 500 mL water' },
        { key: '09:22', text: 'Caf PF 30 gel + 100 mL water (+100 mg)' },
        { key: '~km 25', text: 'Caf PF 30 gel (gel 4 of 6) (+100 mg)' },
        { key: 'Total caf', text: '~300 mg (4.3 mg/kg) · coffee + 2 caf gels' },
      ],
    },
    {
      kind: 'list',
      icon: 'droplet',
      title: 'Hydration & sodium',
      tag: 'targets',
      rows: [
        { key: 'Thu', text: '3.0 L · 3.5 g Na · salt meals' },
        { key: 'Fri', text: '3.5 L · 4.0 g Na · 2× 500 mg electrolyte drinks' },
        { key: 'Sat', text: '3.5 L · taper after 19:00 · 4.0 g Na' },
        { key: 'Pre-race', text: '~1.2 g Na in 1.1 L fluid between 05:00–08:00 (PURE + Na tab)' },
        { key: 'In-race', text: '500–600 mg Na/h · 600–800 mL/h' },
      ],
    },
    {
      kind: 'text',
      icon: 'ban',
      title: 'Do not eat this week',
      tag: 'avoid',
      wide: true,
      text: 'Onion · garlic (use infused oil) · wheat pasta · honey · agave · apple · pear · mango · watermelon · whole cherries · dried fruit >1 tbsp · legumes · cauliflower · mushrooms · sugar-free anything · inulin · chicory · FOS · GOS · Spring Energy · Honey Stinger.',
    },
    {
      kind: 'grid',
      icon: 'alert',
      title: 'If things go sideways',
      tag: 'contingency',
      wide: true,
      grid: [
        {
          title: 'No appetite at 06:00',
          text: 'Minimum 140 g CHO (2 g/kg). 1.5 PURE sachets in water is the rescue. Never skip.',
        },
        { title: 'GI niggle in-race', text: 'Drop to 60 g/h, maltodextrin-only gels. Small performance cost.' },
        { title: 'New-supplement symptom', text: "Skip the next scheduled dose. Don't push through." },
      ],
    },
  ],
  footerTitle: 'Race-week fuelling',
  footerSub: 'Expect to weigh 1.5–2.0 kg more by race morning — that’s stored glycogen and water.',
};
